// Global PWA Install Prompt Manager
// This file MUST be imported early (before React mounts) to capture the beforeinstallprompt event

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

// Store the deferred prompt globally
let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
let installPromptListeners: ((prompt: BeforeInstallPromptEvent | null) => void)[] = [];
let appInstalledListeners: (() => void)[] = [];

// Start listening immediately when this module loads
if (typeof window !== 'undefined') {
  console.log('PWA Install Prompt Manager: Initializing global listener');
  
  window.addEventListener('beforeinstallprompt', (e) => {
    console.log('PWA Install Prompt Manager: beforeinstallprompt event captured!');
    e.preventDefault();
    deferredInstallPrompt = e as BeforeInstallPromptEvent;
    // Notify all listeners
    installPromptListeners.forEach(listener => listener(deferredInstallPrompt));
  });

  window.addEventListener('appinstalled', () => {
    console.log('PWA Install Prompt Manager: App installed!');
    deferredInstallPrompt = null;
    appInstalledListeners.forEach(listener => listener());
  });
}

// Get the stored deferred prompt (if available)
export const getDeferredPrompt = (): BeforeInstallPromptEvent | null => {
  return deferredInstallPrompt;
};

// Clear the deferred prompt (after use)
export const clearDeferredPrompt = (): void => {
  deferredInstallPrompt = null;
};

// Subscribe to prompt availability changes
export const subscribeToPrompt = (
  callback: (prompt: BeforeInstallPromptEvent | null) => void
): (() => void) => {
  installPromptListeners.push(callback);
  // Return unsubscribe function
  return () => {
    installPromptListeners = installPromptListeners.filter(listener => listener !== callback);
  };
};

// Subscribe to app installed event
export const subscribeToAppInstalled = (callback: () => void): (() => void) => {
  appInstalledListeners.push(callback);
  return () => {
    appInstalledListeners = appInstalledListeners.filter(listener => listener !== callback);
  };
};

// Check if install prompt is available
export const isPromptAvailable = (): boolean => {
  return deferredInstallPrompt !== null;
};
